/**
 * Dashboard de métricas da transcrição
 */

import React, { useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  Chip,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import PeopleIcon from '@mui/icons-material/People'
import SpeedIcon from '@mui/icons-material/Speed'
import GavelIcon from '@mui/icons-material/Gavel'
import { Transcription } from '../../../types/transcription'
import {
  calculateMetrics,
  formatDuration,
  findLegalTermsInTranscript,
  extractProcessNumbers,
  extractMonetaryValues,
  extractDates,
  extractCPFs,
  extractRGs,
  extractCNHs,
  extractLegalArticles,
  extractLaws,
  extractSumulas
} from '../../../services/transcriptionAnalysis'
import { CATEGORY_COLORS, getCategoryColor } from '../../../constants/termColors'
import { SpeakerMapping } from '../../../services/exportService'
import { useMergedCustomTerms } from '../../../hooks/useCustomTerms'
import ExtractedDataChip from './ExtractedDataChip'

type Props = {
  transcription: Transcription
  speakerMapping?: SpeakerMapping
  onNavigateToSegment?: (segmentIndex: number) => void
  fileId?: string
  processingTimes?: {
    createdAt?: string | number | null
    processingStartedAt?: number | null
    transcriptionStartedAt?: number | null
    transcriptionCompletedAt?: number | null
    analysisCompletedAt?: number | null
  }
}

export default function MetricsDashboard({ 
  transcription, 
  speakerMapping = {}, 
  onNavigateToSegment,
  fileId,
  processingTimes 
}: Props) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  
  // Busca termos customizados do usuário (GLOBAL + FILE)
  const { terms: customTerms } = useMergedCustomTerms(undefined, fileId)

  // Função para obter o nome do speaker (mapeado ou original)
  const getSpeakerName = (speaker: string) => speakerMapping[speaker] || speaker

  const metrics = useMemo(() => calculateMetrics(transcription), [transcription])
  const legalTerms = useMemo(() => findLegalTermsInTranscript(transcription, customTerms), [transcription, customTerms])
  const termsByCategory = useMemo(() => {
    // Conta termos encontrados por categoria
    const counts: Record<string, number> = {}
    for (const found of legalTerms) {
      const cat = found.category || 'outros'
      counts[cat] = (counts[cat] || 0) + found.occurrences.length
    }
    return counts
  }, [legalTerms])
  
  // Dados extraídos com contexto completo
  const processNumbers = useMemo(() => extractProcessNumbers(transcription), [transcription])
  const monetaryValues = useMemo(() => extractMonetaryValues(transcription), [transcription])
  const dates = useMemo(() => extractDates(transcription), [transcription])
  const cpfs = useMemo(() => extractCPFs(transcription), [transcription])
  const rgs = useMemo(() => extractRGs(transcription), [transcription])
  const cnhs = useMemo(() => extractCNHs(transcription), [transcription])
  const legalArticles = useMemo(() => extractLegalArticles(transcription), [transcription])
  const laws = useMemo(() => extractLaws(transcription), [transcription])
  const sumulas = useMemo(() => extractSumulas(transcription), [transcription])

  const normalizeTimestamp = (value?: string | number | null): number | null => {
    if (value === null || value === undefined) return null
    if (typeof value === 'number') {
      return value < 1_000_000_000_000 ? value * 1000 : value
    }
    if (typeof value === 'string') {
      if (/^\d+$/.test(value)) {
        const num = parseInt(value, 10)
        return num < 1_000_000_000_000 ? num * 1000 : num
      }
      const parsed = Date.parse(value)
      return Number.isNaN(parsed) ? null : parsed
    }
    return null
  }

  const formatProcessingDuration = (ms: number | null) => {
    if (!ms || ms <= 0) return '—'
    return formatDuration(ms / 60000)
  }

  const createdAtTs = normalizeTimestamp(processingTimes?.createdAt)
  const processingStartTs = normalizeTimestamp(processingTimes?.processingStartedAt) ?? createdAtTs
  const transcriptionStartTs =
    normalizeTimestamp(processingTimes?.transcriptionStartedAt) ?? processingStartTs
  const transcriptionCompletedTs = normalizeTimestamp(processingTimes?.transcriptionCompletedAt)
  const analysisCompletedTs = normalizeTimestamp(processingTimes?.analysisCompletedAt)

  const transcriptionDurationMs =
    transcriptionCompletedTs && transcriptionStartTs && transcriptionCompletedTs > transcriptionStartTs
      ? transcriptionCompletedTs - transcriptionStartTs
      : null

  const analysisDurationMs =
    analysisCompletedTs && transcriptionCompletedTs && analysisCompletedTs > transcriptionCompletedTs
      ? analysisCompletedTs - transcriptionCompletedTs
      : null

  const totalProcessingDurationMs =
    analysisCompletedTs && createdAtTs && analysisCompletedTs > createdAtTs
      ? analysisCompletedTs - createdAtTs
      : null

  const totalLegalTerms = legalTerms.reduce((acc, t) => acc + t.occurrences.length, 0)

  return (
    <Box>
      {/* Cards de métricas principais */}
      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={4} sm={4} md={2}>
          <MetricCard
            icon={<AccessTimeIcon sx={{ color: '#1976d2', fontSize: { xs: 18, sm: 24 } }} />}
            label="Duração"
            value={formatDuration(parseFloat(transcription.duration))}
            color="#e3f2fd"
            compact={isMobile}
          />
        </Grid>
        <Grid item xs={4} sm={4} md={2}>
          <MetricCard
            icon={<TextFieldsIcon sx={{ color: '#2e7d32', fontSize: { xs: 18, sm: 24 } }} />}
            label="Palavras"
            value={metrics.totalWords.toLocaleString('pt-BR')}
            color="#e8f5e9"
            compact={isMobile}
          />
        </Grid>
        <Grid item xs={4} sm={4} md={2}>
          <MetricCard
            icon={<FormatListNumberedIcon sx={{ color: '#ed6c02', fontSize: { xs: 18, sm: 24 } }} />}
            label="Segmentos"
            value={metrics.totalSegments.toString()}
            color="#fff3e0"
            compact={isMobile}
          />
        </Grid>
        <Grid item xs={4} sm={4} md={2}>
          <MetricCard
            icon={<PeopleIcon sx={{ color: '#9c27b0', fontSize: { xs: 18, sm: 24 } }} />}
            label={isMobile ? 'Falantes' : 'Participantes'}
            value={metrics.speakerCount.toString()}
            color="#f3e5f5"
            compact={isMobile}
          />
        </Grid>
        <Grid item xs={4} sm={4} md={2}>
          <MetricCard
            icon={<SpeedIcon sx={{ color: '#0288d1', fontSize: { xs: 18, sm: 24 } }} />}
            label={isMobile ? 'Pal/min' : 'Palavras/min'}
            value={Math.round(metrics.wordsPerMinute).toString()}
            color="#e1f5fe"
            compact={isMobile}
          />
        </Grid>
        <Grid item xs={4} sm={4} md={2}>
          <MetricCard
            icon={<GavelIcon sx={{ color: '#c62828', fontSize: { xs: 18, sm: 24 } }} />}
            label={isMobile ? 'Termos' : 'Termos Customizados'}
            value={totalLegalTerms.toString()}
            color="#ffebee"
            compact={isMobile}
          />
        </Grid>
      </Grid>

      {/* Tempos de processamento */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          ⏱️ Tempos de processamento
        </Typography>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid item xs={4} sm={4}>
            <MetricCard
              icon={<AccessTimeIcon sx={{ color: '#1976d2', fontSize: { xs: 18, sm: 24 } }} />}
              label="Transcrição"
              value={formatProcessingDuration(transcriptionDurationMs)}
              color="#e3f2fd"
              compact={isMobile}
            />
          </Grid>
          <Grid item xs={4} sm={4}>
            <MetricCard
              icon={<AccessTimeIcon sx={{ color: '#7b1fa2', fontSize: { xs: 18, sm: 24 } }} />}
              label="Análise"
              value={formatProcessingDuration(analysisDurationMs)}
              color="#f3e5f5"
              compact={isMobile}
            />
          </Grid>
          <Grid item xs={4} sm={4}>
            <MetricCard
              icon={<AccessTimeIcon sx={{ color: '#2e7d32', fontSize: { xs: 18, sm: 24 } }} />}
              label="Total"
              value={formatProcessingDuration(totalProcessingDurationMs)}
              color="#e8f5e9"
              compact={isMobile}
            />
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Participação por Speaker */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0', height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Typography variant="h6" sx={{ mb: { xs: 1.5, sm: 2 }, fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                👥 Participação por Falante
              </Typography>

              <Stack spacing={{ xs: 1.5, sm: 2 }}>
                {metrics.speakerStats.map((stat, index) => {
                  const colors = ['#1976d2', '#9c27b0', '#2e7d32', '#ed6c02', '#d32f2f']
                  const color = colors[index % colors.length]

                  return (
                    <Box key={stat.speaker}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          {getSpeakerName(stat.speaker)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {isMobile ? `${Math.round(stat.percentage)}%` : `${stat.wordCount} palavras • ${Math.round(stat.percentage)}%`}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={stat.percentage}
                        sx={{
                          height: { xs: 6, sm: 8 },
                          borderRadius: { xs: 3, sm: 4 },
                          bgcolor: '#f5f5f5',
                          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: { xs: 3, sm: 4 } }
                        }}
                      />
                    </Box>
                  )
                })}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Termos Customizados por Categoria */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0', height: '100%' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Typography variant="h6" sx={{ mb: { xs: 1.5, sm: 2 }, fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                🏷️ Termos Customizados
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.5, sm: 1 }, mb: { xs: 1.5, sm: 2 } }}>
                {Object.entries(termsByCategory)
                  .filter(([_, count]) => count > 0)
                  .sort((a, b) => b[1] - a[1])
                  .map(([category, count]) => (
                    <Chip
                      key={category}
                      label={`${CATEGORY_COLORS[category]?.label || category}: ${count}`}
                      size="small"
                      sx={{
                        bgcolor: CATEGORY_COLORS[category]?.bg || '#f5f5f5',
                        color: CATEGORY_COLORS[category]?.text || '#333',
                        fontSize: { xs: '0.65rem', sm: '0.8125rem' },
                        height: { xs: 22, sm: 24 }
                      }}
                    />
                  ))}
              </Box>

              <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

              <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {isMobile ? 'Top termos:' : 'Termos mais frequentes:'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {legalTerms.slice(0, isMobile ? 6 : 10).map((term) => (
                  <Chip
                    key={term.term}
                    label={`${term.term} (${term.occurrences.length})`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, height: { xs: 20, sm: 24 } }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Extração de Dados */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Typography variant="h6" sx={{ mb: { xs: 1.5, sm: 2 }, fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                📋 Dados Extraídos
              </Typography>

              {/* Números de Processo */}
              {processNumbers.length > 0 && (
                <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.75, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {isMobile ? 'Processos:' : 'Números de Processo:'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {processNumbers.slice(0, isMobile ? 2 : processNumbers.length).map((data, i) => (
                      <ExtractedDataChip
                        key={i}
                        data={data}
                        color="primary"
                        onNavigate={onNavigateToSegment}
                        speakerMapping={speakerMapping}
                        isMobile={isMobile}
                      />
                    ))}
                    {isMobile && processNumbers.length > 2 && (
                      <Chip
                        label={`+${processNumbers.length - 2}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 22 }}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Valores Monetários */}
              {monetaryValues.length > 0 && (
                <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.75, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Valores Monetários:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {monetaryValues.slice(0, isMobile ? 3 : monetaryValues.length).map((data, i) => (
                      <ExtractedDataChip
                        key={i}
                        data={data}
                        color="success"
                        onNavigate={onNavigateToSegment}
                        speakerMapping={speakerMapping}
                        isMobile={isMobile}
                      />
                    ))}
                    {isMobile && monetaryValues.length > 3 && (
                      <Chip
                        label={`+${monetaryValues.length - 3}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 22 }}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Datas */}
              {dates.length > 0 && (
                <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.75, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Datas Mencionadas:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {dates.slice(0, isMobile ? 3 : dates.length).map((data, i) => (
                      <ExtractedDataChip
                        key={i}
                        data={data}
                        color="warning"
                        onNavigate={onNavigateToSegment}
                        speakerMapping={speakerMapping}
                        isMobile={isMobile}
                      />
                    ))}
                    {isMobile && dates.length > 3 && (
                      <Chip
                        label={`+${dates.length - 3}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 22 }}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Documentos (CPF, RG, CNH) */}
              {(cpfs.length > 0 || rgs.length > 0 || cnhs.length > 0) && (
                <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.75, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Documentos:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {cpfs.map((data, i) => (
                      <ExtractedDataChip
                        key={`cpf-${i}`}
                        data={data}
                        color="info"
                        label={`CPF: ${data.value}`}
                        onNavigate={onNavigateToSegment}
                        speakerMapping={speakerMapping}
                        isMobile={isMobile}
                      />
                    ))}
                    {rgs.map((data, i) => (
                      <ExtractedDataChip
                        key={`rg-${i}`}
                        data={data}
                        color="info"
                        label={`RG: ${data.value}`}
                        onNavigate={onNavigateToSegment}
                        speakerMapping={speakerMapping}
                        isMobile={isMobile}
                      />
                    ))}
                    {cnhs.map((data, i) => (
                      <ExtractedDataChip
                        key={`cnh-${i}`}
                        data={data}
                        color="info"
                        label={`CNH: ${data.value}`}
                        onNavigate={onNavigateToSegment}
                        speakerMapping={speakerMapping}
                        isMobile={isMobile}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Referências Legais */}
              {(legalArticles.length > 0 || laws.length > 0 || sumulas.length > 0) && (
                <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.75, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Referências Legais:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {legalArticles.slice(0, isMobile ? 3 : legalArticles.length).map((data, i) => (
                      <ExtractedDataChip
                        key={`art-${i}`}
                        data={data}
                        color="secondary"
                        onNavigate={onNavigateToSegment}
                        speakerMapping={speakerMapping}
                        isMobile={isMobile}
                      />
                    ))}
                    {laws.slice(0, isMobile ? 2 : laws.length).map((data, i) => (
                      <ExtractedDataChip
                        key={`law-${i}`}
                        data={data}
                        color="secondary"
                        onNavigate={onNavigateToSegment}
                        speakerMapping={speakerMapping}
                        isMobile={isMobile}
                      />
                    ))}
                    {sumulas.map((data, i) => (
                      <ExtractedDataChip
                        key={`sum-${i}`}
                        data={data}
                        color="secondary"
                        onNavigate={onNavigateToSegment}
                        speakerMapping={speakerMapping}
                        isMobile={isMobile}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Tópicos */}
              {transcription.topics.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.75, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Tópicos:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {transcription.topics.slice(0, isMobile ? 3 : transcription.topics.length).map((topic, i) => (
                      <Chip
                        key={i}
                        label={topic}
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.8125rem' }, height: { xs: 22, sm: 24 } }}
                      />
                    ))}
                    {isMobile && transcription.topics.length > 3 && (
                      <Chip
                        label={`+${transcription.topics.length - 3}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 22 }}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {processNumbers.length === 0 && 
               monetaryValues.length === 0 && 
               dates.length === 0 &&
               cpfs.length === 0 &&
               rgs.length === 0 &&
               cnhs.length === 0 &&
               legalArticles.length === 0 &&
               laws.length === 0 &&
               sumulas.length === 0 &&
               transcription.topics.length === 0 && (
                <Typography variant="body2" sx={{ color: '#999', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  Nenhum dado específico extraído automaticamente.
                </Typography>
              )}

              {/* Dica de uso */}
              {(processNumbers.length > 0 || monetaryValues.length > 0 || dates.length > 0 || 
                cpfs.length > 0 || rgs.length > 0 || cnhs.length > 0 || 
                legalArticles.length > 0 || laws.length > 0 || sumulas.length > 0) && (
                <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #f0f0f0' }}>
                  <Typography variant="caption" sx={{ color: '#999', fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
                    💡 {isMobile ? 'Toque nos chips para ver detalhes' : 'Clique nos chips para ver todas as menções e navegar até o contexto na transcrição'}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Estatísticas Adicionais */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Typography variant="h6" sx={{ mb: { xs: 1.5, sm: 2 }, fontWeight: 600, fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                📊 Estatísticas Adicionais
              </Typography>

              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={6}>
                  <StatItem
                    label={isMobile ? 'Média/segmento' : 'Média por segmento'}
                    value={`${Math.round(metrics.averageWordsPerSegment)} palavras`}
                    compact={isMobile}
                  />
                </Grid>
                <Grid item xs={6}>
                  <StatItem
                    label="Duração média"
                    value={`${metrics.averageSegmentDuration.toFixed(1)}s`}
                    compact={isMobile}
                  />
                </Grid>
                <Grid item xs={6}>
                  <StatItem
                    label="Fonte"
                    value={transcription.transcript_source}
                    compact={isMobile}
                  />
                </Grid>
                <Grid item xs={6}>
                  <StatItem
                    label="ID"
                    value={transcription.meeting_id.substring(0, isMobile ? 6 : 8) + '...'}
                    compact={isMobile}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

function MetricCard({
  icon,
  label,
  value,
  color,
  compact = false
}: {
  icon: React.ReactNode
  label: string
  value: string
  color: string
  compact?: boolean
}) {
  return (
    <Card elevation={0} sx={{ border: '1px solid #e0e0e0', height: '100%' }}>
      <CardContent sx={{ textAlign: 'center', py: compact ? 1 : 2, px: compact ? 1 : 2, '&:last-child': { pb: compact ? 1 : 2 } }}>
        <Box
          sx={{
            width: compact ? 32 : 48,
            height: compact ? 32 : 48,
            borderRadius: '50%',
            bgcolor: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: compact ? 0.5 : 1
          }}
        >
          {icon}
        </Box>
        <Typography
          variant={compact ? 'subtitle1' : 'h5'}
          sx={{ fontWeight: 700, fontSize: compact ? '0.95rem' : '1.5rem', lineHeight: 1.2 }}
        >
          {value}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: '#666', fontSize: compact ? '0.65rem' : '0.75rem', lineHeight: 1.2, display: 'block' }}
        >
          {label}
        </Typography>
      </CardContent>
    </Card>
  )
}

function StatItem({ label, value, compact = false }: { label: string; value: string; compact?: boolean }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: '#666', fontSize: compact ? '0.65rem' : '0.75rem' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: compact ? '0.8rem' : '0.875rem' }}>
        {value}
      </Typography>
    </Box>
  )
}
